import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import type { TableColumn } from '@/types/compatibility'

interface ColumnFilterProps {
  column: TableColumn
  value: string
  onChange: (value: string, operator: string) => void
  onClear: () => void
}

interface FilterPopupProps {
  column: TableColumn
  value: string
  operator: string
  onApply: (value: string, operator: string) => void
  onClose: () => void
  anchorEl: HTMLElement | null
}

const FilterPopup: React.FC<FilterPopupProps> = ({ column, value, operator, onApply, onClose, anchorEl }) => {
  const [localValue, setLocalValue] = useState(value)
  const [localOperator, setLocalOperator] = useState(operator)
  const popupRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [arrowPosition, setArrowPosition] = useState({ left: 0 })

  useEffect(() => {
    if (anchorEl && popupRef.current) {
      const rect = anchorEl.getBoundingClientRect()
      const popupRect = popupRef.current.getBoundingClientRect()
      
      // Calculate position to center below the anchor element
      let left = rect.left + (rect.width / 2) - (popupRect.width / 2)
      let top = rect.bottom + 8  // Increased spacing for arrow
      
      // Adjust if popup goes off screen
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      
      if (left + popupRect.width > viewportWidth - 10) {
        left = viewportWidth - popupRect.width - 10
      }
      if (left < 10) {
        left = 10
      }
      
      if (top + popupRect.height > viewportHeight - 10) {
        // Show above if not enough space below
        top = rect.top - popupRect.height - 8
      }
      
      // Calculate arrow position relative to the anchor
      const arrowLeft = rect.left + (rect.width / 2) - 6  // 6 is half the arrow width
      
      setPosition({ top, left })
      setArrowPosition({ left: arrowLeft })
    }
  }, [anchorEl])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const { target } = event
      if (popupRef.current && target instanceof Node &&
          anchorEl && !anchorEl.contains(target) && !popupRef.current.contains(target)) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose, anchorEl])

  const handleApply = () => {
    onApply(localValue, localOperator)
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleApply()
    }
  }

  const getOperatorOptions = () => {
    const dataType = column.dataType?.toLowerCase() || ''
    
    if (dataType.includes('int') || dataType.includes('decimal') || 
        dataType.includes('float') || dataType.includes('double') || 
        dataType.includes('numeric') || dataType.includes('real')) {
      return [
        { value: '=', label: '=' },
        { value: '!=', label: '≠' },
        { value: '>', label: '>' },
        { value: '<', label: '<' },
        { value: '>=', label: '≥' },
        { value: '<=', label: '≤' },
        { value: 'IS NULL', label: 'Is Empty' },
        { value: 'IS NOT NULL', label: 'Not Empty' }
      ]
    }
    
    if (dataType.includes('date') || dataType.includes('time')) {
      return [
        { value: '=', label: 'Equals' },
        { value: '!=', label: 'Not Equals' },
        { value: '>', label: 'After' },
        { value: '<', label: 'Before' },
        { value: '>=', label: 'On or After' },
        { value: '<=', label: 'On or Before' },
        { value: 'IS NULL', label: 'Is Empty' },
        { value: 'IS NOT NULL', label: 'Not Empty' }
      ]
    }
    
    if (dataType.includes('bool')) {
      return [
        { value: '=', label: 'Equals' },
        { value: '!=', label: 'Not Equals' },
        { value: 'IS NULL', label: 'Is Empty' },
        { value: 'IS NOT NULL', label: 'Not Empty' }
      ]
    }
    
    return [
      { value: 'LIKE', label: 'Contains' },
      { value: 'NOT LIKE', label: 'Not Contains' },
      { value: '=', label: 'Equals' },
      { value: '!=', label: 'Not Equals' },
      { value: 'IS NULL', label: 'Is Empty' },
      { value: 'IS NOT NULL', label: 'Not Empty' }
    ]
  }

  const renderValueInput = () => {
    if (localOperator === 'IS NULL' || localOperator === 'IS NOT NULL') {
      return null
    }

    const dataType = column.dataType?.toLowerCase() || ''
    
    if (dataType.includes('bool')) {
      return (
        <select
          value={localValue}
          onChange={(e) => { setLocalValue(e.target.value) }}
          onKeyDown={handleKeyDown}
          className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          autoFocus
        >
          <option value="">Select...</option>
          <option value="true">TRUE</option>
          <option value="false">FALSE</option>
          <option value="1">1</option>
          <option value="0">0</option>
        </select>
      )
    }
    
    if (dataType.includes('date') && !dataType.includes('time')) {
      return (
        <input
          type="date"
          value={localValue}
          onChange={(e) => { setLocalValue(e.target.value) }}
          onKeyDown={handleKeyDown}
          className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          autoFocus
        />
      )
    }
    
    if (dataType.includes('datetime') || dataType.includes('timestamp')) {
      return (
        <input
          type="datetime-local"
          value={localValue}
          onChange={(e) => { setLocalValue(e.target.value) }}
          onKeyDown={handleKeyDown}
          className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          autoFocus
        />
      )
    }
    
    if (dataType.includes('time')) {
      return (
        <input
          type="time"
          value={localValue}
          onChange={(e) => { setLocalValue(e.target.value) }}
          onKeyDown={handleKeyDown}
          className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          autoFocus
        />
      )
    }
    
    if (dataType.includes('int') || dataType.includes('decimal') || 
        dataType.includes('float') || dataType.includes('double') || 
        dataType.includes('numeric') || dataType.includes('real')) {
      return (
        <input
          type="number"
          value={localValue}
          onChange={(e) => { setLocalValue(e.target.value) }}
          onKeyDown={handleKeyDown}
          placeholder="Enter number..."
          className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          autoFocus
        />
      )
    }
    
    return (
      <input
        type="text"
        value={localValue}
        onChange={(e) => { setLocalValue(e.target.value) }}
        onKeyDown={handleKeyDown}
        placeholder="Enter value..."
        className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
        autoFocus
      />
    )
  }

  return createPortal(
    <>
      {/* Arrow pointing to the input */}
      <div
        className="fixed z-50 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-gray-200 dark:border-b-gray-700"
        style={{ 
          top: position.top - 6, 
          left: arrowPosition.left
        }}
      />
      <div
        className="fixed z-50 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[5px] border-b-white dark:border-b-gray-800"
        style={{ 
          top: position.top - 5, 
          left: arrowPosition.left + 1
        }}
      />
      <div
        ref={popupRef}
        className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 w-72"
        style={{ top: position.top, left: position.left }}
      >
        <div className="mb-2">
        <h3 className="text-xs font-semibold text-gray-900 dark:text-white">
          {column.name}
        </h3>
        <p className="text-[10px] text-gray-500 dark:text-gray-400">
          {column.dataType || 'text'}
        </p>
      </div>
      
      <div className="space-y-2">
        <div>
          <label className="block text-[10px] font-medium text-gray-700 dark:text-gray-300 mb-1">
            Condition
          </label>
          <select
            value={localOperator}
            onChange={(e) => { setLocalOperator(e.target.value) }}
            onKeyDown={handleKeyDown}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
          >
            {getOperatorOptions().map(op => (
              <option key={op.value} value={op.value}>{op.label}</option>
            ))}
          </select>
        </div>
        
        {renderValueInput() && (
          <div>
            <label className="block text-[10px] font-medium text-gray-700 dark:text-gray-300 mb-1">
              Value
            </label>
            {renderValueInput()}
          </div>
        )}
        
        <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
          <span className="text-[9px] text-gray-400 dark:text-gray-500">Press Enter to apply</span>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
    </>,
    document.body
  )
}

export const ColumnFilter: React.FC<ColumnFilterProps> = ({ column, value, onChange, onClear }) => {
  const [showPopup, setShowPopup] = useState(false)
  const [currentOperator, setCurrentOperator] = useState(() => {
    const dataType = column.dataType?.toLowerCase() || ''
    // Set default operator based on data type
    if (dataType.includes('char') || dataType.includes('text') || dataType.includes('string')) {
      return 'LIKE'
    }
    return '='
  })
  const filterRef = useRef<HTMLDivElement>(null)

  const dataType = column.dataType?.toLowerCase() || ''
  
  const isUnsupportedType = useCallback(() => {
    return dataType.includes('json') || 
           dataType.includes('blob') || 
           dataType.includes('binary') || 
           dataType.includes('bytea') ||
           dataType.includes('geometry') ||
           dataType.includes('geography') ||
           dataType.includes('xml') ||
           dataType.includes('array') ||
           dataType.includes('hstore')
  }, [dataType])

  const handlePopupApply = useCallback((newValue: string, operator: string) => {
    setCurrentOperator(operator)
    onChange(newValue, operator)
  }, [onChange])

  const handleInputClick = useCallback(() => {
    setShowPopup(true)
  }, [])

  const getDisplayText = useCallback(() => {
    if (!value && currentOperator !== 'IS NULL' && currentOperator !== 'IS NOT NULL') {
      return '...'
    }
    
    // Show operator for clarity when there's a value
    if (currentOperator === 'LIKE') {
      return value.length > 10 ? `*${value.substring(0, 10)}...*` : `*${value}*`
    } else if (currentOperator === 'NOT LIKE') {
      return value.length > 8 ? `!*${value.substring(0, 8)}...*` : `!*${value}*`
    } else if (currentOperator === 'IS NULL') {
      return 'empty'
    } else if (currentOperator === 'IS NOT NULL') {
      return 'not empty'
    } else if (currentOperator === '=') {
      return value.length > 12 ? `${value.substring(0, 12)}...` : value
    } else if (currentOperator === '!=') {
      return value.length > 10 ? `≠${value.substring(0, 10)}...` : `≠${value}`
    } else if (currentOperator === '>') {
      return `>${value}`
    } else if (currentOperator === '<') {
      return `<${value}`
    } else if (currentOperator === '>=') {
      return `≥${value}`
    } else if (currentOperator === '<=') {
      return `≤${value}`
    }
    return value
  }, [value, currentOperator])

  if (isUnsupportedType()) {
    return (
      <div className="text-[10px] text-gray-400 dark:text-gray-500 italic text-center">
        N/A
      </div>
    )
  }

  return (
    <>
      <div ref={filterRef} className="relative group">
        <div
          onClick={handleInputClick}
          className={`flex items-center justify-between px-1.5 py-0.5 text-[11px] border-0 border-b cursor-pointer transition-colors ${
            value 
              ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400' 
              : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600 text-gray-400 dark:text-gray-500'
          }`}
          title={value ? `Filter: ${getDisplayText()}` : 'Click to add filter'}
        >
          <span className="truncate flex-1 text-left">
            {getDisplayText()}
          </span>
          <div className="flex items-center ml-1">
            {value && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onClear()
                }}
                className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Clear filter"
              >
                <XMarkIcon className="w-2.5 h-2.5" />
              </button>
            )}
            <ChevronDownIcon className="w-2.5 h-2.5 ml-0.5" />
          </div>
        </div>
      </div>
      
      {showPopup && (
        <FilterPopup
          column={column}
          value={value}
          operator={currentOperator}
          onApply={handlePopupApply}
          onClose={() => { setShowPopup(false) }}
          anchorEl={filterRef.current}
        />
      )}
    </>
  )
}